import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders, HttpResponse} from "@angular/common/http";
import { AuthService } from '../auth/services/auth.service';
import {from, map, Observable, switchMap, throwError} from 'rxjs';
import {catchError} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export abstract class ApiService {
  protected constructor(
      protected http: HttpClient,
      protected authService: AuthService
  ) {}

  protected getHeaders(): Observable<HttpHeaders> {
    return from(this.authService.getToken()).pipe(
        map(
            (token) =>
                new HttpHeaders({
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                })
        )
    );
  }

  // para no enviar content type y subir archivos
  public getHeadersFile(): Observable<HttpHeaders> {
    return from(this.authService.getToken()).pipe(
        map(
            (token) =>
                new HttpHeaders({
                  Authorization: `Bearer ${token}`,
                })
        )
    );
  }

  protected handleError(error: any): Observable<never> {
    // Reenviamos el error original (HttpErrorResponse) para conservar status y body.
    // Antes se envolvía en new Error(error), que perdía ambos.
    console.error(error);
    return throwError(() => error);
  }

  protected get<T>(url: string): Observable<T> {
    return this.getHeaders().pipe(
        switchMap((headers) =>
            this.http.get<T>(url, { headers }).pipe(
                map((value) => value as T),
                catchError(this.handleError)
            )
        )
    );
  }

  protected post<T>(
      url: string,
      data: any,
      customHeaders: any = null,
  ): Observable<T> {
    return this.getHeaders().pipe(
        switchMap((headers) => {
          const finalHeaders = customHeaders ? customHeaders : headers;
          return this.http.post<T>(url, data, { headers: finalHeaders }).pipe(
              map((value) => value as T),
              catchError(this.handleError)
          );
        })
    );
  }

  protected  postExcel(url: string, data: any): Observable<HttpResponse<any>> {
    return this.getHeaders().pipe(
        switchMap((headers) => {
          const finalHeaders = headers
              .append('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
          return this.http.post(url, data, {
            headers: finalHeaders,
            responseType: 'blob',
            observe: 'response'
          }).pipe(catchError(this.handleError));
        })
    );
  }

  protected put<T>(url: string, data: any): Observable<T> {
    return this.getHeaders().pipe(
        switchMap((headers) =>
            this.http.put<T>(url, data, { headers }).pipe(
                map((value) => value as T),
                catchError(this.handleError)
            )
        )
    );
  }

  protected patch<T>(url: string, data: any): Observable<T> {
    return this.getHeaders().pipe(
        switchMap((headers) =>
            this.http.patch<T>(url, data, { headers }).pipe(
                map((value) => value as T),
                catchError(this.handleError)
            )
        )
    );
  }

  protected delete<T>(url: string): Observable<boolean> {
    return this.getHeaders().pipe(
        switchMap((headers) =>
            this.http
                .delete<boolean>(url, { headers })
                .pipe(catchError(this.handleError))
        )
    );
  }
}
